import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  Calendar, CheckCircle, Circle, Clock, AlertCircle, 
  TrendingUp, FileText, Target, Flag, ChevronDown, ChevronUp 
} from 'lucide-react';
import { toast } from 'sonner';



const PHASE_COLORS = {
  research: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  preparation: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  decision: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  action: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  final: 'bg-green-500/10 text-green-400 border-green-500/20',
  complete: 'bg-primary/10 text-primary border-primary/20'
};

const PHASE_ICONS = {
  research: TrendingUp,
  preparation: FileText,
  decision: Target,
  action: Flag,
  final: CheckCircle,
  complete: CheckCircle
};

const CATEGORY_LABELS = {
  research: 'Research',
  action: 'Action Required',
  decision: 'Decision Point',
  final: 'Final Steps'
};

const RenewalTimeline = ({ mortgage }) => {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [updating, setUpdating] = useState(null);
  
  useEffect(() => {
    if (mortgage?.renewal_date) {
      fetchTimeline();
    } else {
      setLoading(false);
    }
  }, [mortgage?.renewal_date]);
  
  const fetchTimeline = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/renewal-timeline`);
      setTimeline(response.data);
      // Auto-expand current phase
      if (response.data?.current_phase) {
        setExpandedPhase(response.data.current_phase);
      }
    } catch (error) {
      console.error('Error fetching timeline:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load renewal timeline');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const toggleTask = async (taskId, currentStatus) => {
    try {
      setUpdating(taskId);
      await api.post(`/api/renewal-timeline/task`, {
        task_id: taskId,
        completed: !currentStatus
      });
      
      // Update local state
      setTimeline(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => 
          t.task_id === taskId ? { ...t, completed: !currentStatus } : t
        ),
        progress_percentage: prev.tasks.filter(t => 
          t.task_id === taskId ? !currentStatus : t.completed
        ).length / prev.tasks.length * 100
      }));
      
      toast.success(!currentStatus ? 'Task completed!' : 'Task marked incomplete');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    } finally {
      setUpdating(null);
    }
  };
  
  const getPhaseTitle = (phase) => {
    const titles = {
      research: 'Research Phase',
      preparation: 'Preparation Phase',
      decision: 'Decision Phase',
      action: 'Action Phase',
      final: 'Final Preparations',
      complete: 'Renewal Complete'
    };
    return titles[phase] || phase;
  };
  
  const groupTasksByPhase = (tasks) => {
    const grouped = {
      research: [],
      preparation: [],
      decision: [],
      action: [],
      final: []
    };
    
    tasks.forEach(task => {
      if (task.days_before > 120) {
        grouped.research.push(task);
      } else if (task.days_before > 90) {
        grouped.preparation.push(task);
      } else if (task.days_before > 60) {
        grouped.decision.push(task);
      } else if (task.days_before > 30) {
        grouped.action.push(task);
      } else {
        grouped.final.push(task);
      }
    });
    
    return grouped;
  };
  
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  
  if (!mortgage?.renewal_date) {
    return (
      <div data-testid="renewal-timeline" className="bg-card border border-border rounded-xl p-6">
        <div className="text-center py-8">
          <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Set Your Renewal Date</h3>
          <p className="text-muted-foreground text-sm">
            Add your mortgage renewal date in the details form above to see your personalized timeline.
          </p>
        </div>
      </div>
    );
  }
  
  if (!timeline) {
    return (
      <div data-testid="renewal-timeline" className="bg-card border border-border rounded-xl p-6">
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Unable to load timeline. Please try again.</p>
        </div>
      </div>
    );
  }
  
  const groupedTasks = groupTasksByPhase(timeline.tasks);
  const PhaseIcon = PHASE_ICONS[timeline.current_phase] || Calendar;
  
  return (
    <div data-testid="renewal-timeline" className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-heading font-semibold flex items-center gap-2">
          <Calendar className="h-6 w-6 text-primary" />
          Renewal Timeline
        </h3>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border ${PHASE_COLORS[timeline.current_phase]}`}>
          <PhaseIcon className="h-4 w-4" />
          {getPhaseTitle(timeline.current_phase)}
        </div>
      </div>
      
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-5 w-5 text-primary" />
            <p className="text-sm text-muted-foreground">Days Until Renewal</p>
          </div>
          <p className="text-3xl font-bold font-mono">
            {timeline.days_until_renewal}
          </p>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-5 w-5 text-primary" />
            <p className="text-sm text-muted-foreground">Renewal Date</p>
          </div>
          <p className="text-lg font-semibold">
            {new Date(timeline.renewal_date).toLocaleDateString('en-CA', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric' 
            })}
          </p>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <p className="text-sm text-muted-foreground">Tasks Completed</p>
          </div>
          <p className="text-3xl font-bold font-mono">
            {timeline.tasks.filter(t => t.completed).length}/{timeline.tasks.length}
          </p>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <p className="text-sm text-muted-foreground">Progress</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${timeline.progress_percentage}%` }}
              />
            </div>
            <span className="text-sm font-mono">{timeline.progress_percentage.toFixed(0)}%</span>
          </div>
        </div>
      </div>
      
      {/* Next Milestone Alert */}
      {timeline.next_milestone && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-primary">Next: {timeline.next_milestone.title}</p>
            <p className="text-sm text-muted-foreground">
              {timeline.next_milestone.days_until_due > 0 
                ? `Due in ${timeline.next_milestone.days_until_due} days`
                : 'Due now'}
            </p>
          </div>
        </div>
      )}
      
      {/* Phase Timeline */}
      <div className="space-y-4">
        {Object.entries(groupedTasks).map(([phase, tasks]) => {
          if (tasks.length === 0) return null;
          
          const isExpanded = expandedPhase === phase;
          const completedInPhase = tasks.filter(t => t.completed).length;
          const isCurrentPhase = timeline.current_phase === phase;
          const PhaseIconComponent = PHASE_ICONS[phase] || Circle;
          
          return (
            <div 
              key={phase}
              className={`bg-card border rounded-xl overflow-hidden transition-all ${
                isCurrentPhase ? 'border-primary' : 'border-border'
              }`}
            >
              {/* Phase Header */}
              <button
                onClick={() => setExpandedPhase(isExpanded ? null : phase)}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${PHASE_COLORS[phase]}`}>
                    <PhaseIconComponent className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{getPhaseTitle(phase)}</span>
                      {isCurrentPhase && (
                        <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {completedInPhase}/{tasks.length} tasks completed
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${(completedInPhase / tasks.length) * 100}%` }}
                    />
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </button>
              
              {/* Tasks */}
              {isExpanded && (
                <div className="border-t border-border divide-y divide-border">
                  {tasks.map((task) => (
                    <div 
                      key={task.task_id}
                      className={`flex items-start gap-4 p-4 transition-colors ${
                        task.completed ? 'bg-muted/20' : 'hover:bg-muted/10'
                      }`}
                    >
                      <button
                        onClick={() => toggleTask(task.task_id, task.completed)}
                        disabled={updating === task.task_id}
                        className={`mt-0.5 flex-shrink-0 transition-colors ${
                          updating === task.task_id ? 'opacity-50' : ''
                        }`}
                      >
                        {task.completed ? (
                          <CheckCircle className="h-6 w-6 text-green-400" />
                        ) : (
                          <Circle className="h-6 w-6 text-muted-foreground hover:text-primary" />
                        )}
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                            {task.title}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            task.priority === 'high' 
                              ? 'bg-red-500/10 text-red-400' 
                              : task.priority === 'medium'
                                ? 'bg-yellow-500/10 text-yellow-400'
                                : 'bg-muted text-muted-foreground'
                          }`}>
                            {task.priority}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {CATEGORY_LABELS[task.category]}
                          </span>
                        </div>
                        <p className={`text-sm mt-1 ${task.completed ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                          {task.description}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Recommended: {task.days_before} days before renewal
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Tips */}
      <div className="bg-muted/30 border border-border rounded-xl p-4">
        <h4 className="font-semibold mb-2 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-primary" />
          Pro Tips
        </h4>
        <ul className="space-y-1 text-sm text-muted-foreground">
          <li>• Most lenders allow you to lock in rates 90-120 days before renewal</li>
          <li>• Switching lenders is often free - no penalty for leaving at renewal</li>
          <li>• Always negotiate - your current lender may offer better rates to keep you</li>
          <li>• Consider your total cost including fees, not just the interest rate</li>
        </ul>
      </div>
    </div>
  );
};

export default RenewalTimeline;
